/**
 * OAuth callback page
 * Handles the redirect from management API after it sets the auth cookie.
 *
 * Flow:
 * 1. User completes OAuth with provider (GitHub/Entra)
 * 2. Management API sets HttpOnly cookie on its domain and redirects here
 * 3. This page fetches user info and completes login
 *
 * The auth token is in an HttpOnly cookie on the management API domain.
 * The browser automatically sends it with every request to management API.
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Spinner } from '@/components/ui';
import { verifyOAuthState, saveUserData, AuthProvider } from '@/services/authService';
import { httpClient } from '@/services/httpClient';
import { useAuth } from '@/contexts/AuthContext';

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double-processing in React StrictMode or due to dependency changes
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const provider = (searchParams.get('provider') as AuthProvider) || 'github';

      // Check for OAuth errors
      if (errorParam) {
        setError(`OAuth error: ${errorParam}`);
        return;
      }

      if (!state) {
        setError('Missing state parameter');
        return;
      }

      // Verify state to prevent CSRF
      if (!verifyOAuthState(state, provider)) {
        setError('Invalid state parameter - possible CSRF attack');
        return;
      }

      try {
        // The auth cookie is already set on the management API domain by the OAuth callback.
        // Fetch user info from management API - the cookie is sent automatically by the browser.
        console.log('[AuthCallback] Fetching user info from management API');

        const response = await httpClient.get('/auth/me');
        const user = response.data;
        console.log('[AuthCallback] User info fetched:', user.login || user.email);

        // Save user data to localStorage for display
        saveUserData(user, provider);

        // Update context with user info
        login(user);

        console.log('[AuthCallback] Login successful');

        // Redirect to home
        navigate('/', { replace: true });
      } catch (err) {
        console.error('[AuthCallback] Login failed:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    handleCallback();
  }, [searchParams, navigate, login]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-700">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-2xl font-semibold text-white mb-4">Authentication Failed</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="text-center">
        <Spinner size="lg" className="mb-4" />
        <h2 className="text-2xl font-semibold text-white mb-2">Completing sign in...</h2>
        <p className="text-gray-400">Please wait while we authenticate your account</p>
      </div>
    </div>
  );
}
